import { useState } from 'react';
import { Box, Checkbox, CircularProgress, FormControlLabel, Typography } from '@mui/material';
import MDButton from '@components/MDButton';
import { useAlert } from '@components/showAlert/useAlertHook';
import { useTranslation } from 'react-i18next';
import { apiErrorMessage } from '@functions/api-error-message';
import { AiLeadInsights } from '@services/ai.service';
import { getCrmColors } from '@layouts/pages/CRM/kanban-board/crm-theme';
import { CrmLeadService } from '@layouts/pages/CRM/kanban-board/services/crm-lead.service';
import { CrmScheduleService } from '@layouts/pages/CRM/kanban-board/services/crm-schedule.service';

type Field = 'value' | 'expectedCloseDate' | 'email' | 'cnpj' | 'companyName' | 'nextStep';

const LABEL: Record<Field, string> = {
    value: 'ia.campoValor',
    expectedCloseDate: 'ia.campoPrevisao',
    email: 'ia.campoEmail',
    cnpj: 'ia.campoCnpj',
    companyName: 'ia.campoEmpresa',
    nextStep: 'ia.campoCompromisso',
};

const FIELDS: Field[] = ['value', 'expectedCloseDate', 'companyName', 'cnpj', 'email', 'nextStep'];

const money = (value: number) =>
    value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const day = (value: string) => {
    const [year, month, date] = value.slice(0, 10).split('-');
    return `${date}/${month}/${year}`;
};

const cnpjMask = (digits: string) =>
    digits.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');

const show = (field: Field, found: AiLeadInsights): string => {
    switch (field) {
        case 'value': return money(found.value!);
        case 'expectedCloseDate': return day(found.expectedCloseDate!);
        case 'cnpj': return cnpjMask(found.cnpj!);
        case 'nextStep':
            return `${day(found.nextStep!.scheduledAt)} ${found.nextStep!.scheduledAt.slice(11, 16)}`
                + ` · ${found.nextStep!.description}`;
        default: return found[field] as string;
    }
};

interface Props {
    leadId: string;
    found: AiLeadInsights;
    cc: ReturnType<typeof getCrmColors>;
    onApplied: () => void;
    onDiscard: () => void;
}

/// Sugestões da IA para completar o lead, todas marcadas: desmarcar custa menos que marcar.
export const AiInsightsResult = ({ leadId, found, cc, onApplied, onDiscard }: Props) => {
    const { t } = useTranslation();
    const { showAlert } = useAlert();

    const available = FIELDS.filter((field) => found[field] !== null);
    const [checked, setChecked] = useState<Set<Field>>(() => new Set(available));
    const [saving, setSaving] = useState(false);

    const toggle = (field: Field) => setChecked((current) => {
        const next = new Set(current);
        if (next.has(field)) next.delete(field); else next.add(field);
        return next;
    });

    const pick = <K extends Field>(field: K) =>
        (checked.has(field) ? found[field] : null) as AiLeadInsights[K] | null;

    const apply = async () => {
        setSaving(true);

        try {
            const body = {
                value: pick('value'),
                expectedCloseDate: pick('expectedCloseDate'),
                email: pick('email'),
                cnpj: pick('cnpj'),
                companyName: pick('companyName'),
            };

            if (Object.values(body).some((value) => value !== null)) {
                await new CrmLeadService().complete(leadId, body);
            }

            const step = pick('nextStep');

            if (step) {
                // Sem fuso o navegador lê como hora local, a mesma que o cliente escreveu.
                await new CrmScheduleService().create({
                    leadId,
                    description: step.description,
                    scheduledAt: new Date(step.scheduledAt).toISOString(),
                });
            }

            showAlert(t('ia.aplicado'), 'success', 2500);
            onApplied();
        } catch (err) {
            showAlert(apiErrorMessage(err), 'error', 5000);
        } finally {
            setSaving(false);
        }
    };

    if (available.length === 0) {
        return (
            <Typography sx={{ fontSize: 12.5, color: cc.textSecondary }}>
                {t('ia.nadaNovo')}
            </Typography>
        );
    }

    return (
        <>
            <Box sx={{ display: 'grid' }}>
                {available.map((field) => (
                    <FormControlLabel
                        key={field}
                        sx={{ m: 0, alignItems: 'flex-start' }}
                        control={(
                            <Checkbox
                                size="small"
                                checked={checked.has(field)}
                                onChange={() => toggle(field)}
                                sx={{ p: 0.5, mr: 0.5 }}
                            />
                        )}
                        label={(
                            <Box sx={{ pt: 0.4 }}>
                                <Typography sx={{ fontSize: 11, color: cc.textMuted, fontWeight: 700 }}>
                                    {t(LABEL[field])}
                                </Typography>
                                <Typography sx={{ fontSize: 13, color: cc.textPrimary, overflowWrap: 'anywhere' }}>
                                    {show(field, found)}
                                </Typography>
                            </Box>
                        )}
                    />
                ))}
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1 }}>
                <MDButton size="small" variant="text" color="dark" onClick={onDiscard} disabled={saving}>
                    {t('ia.descartar')}
                </MDButton>
                <MDButton
                    size="small"
                    variant="gradient"
                    color="success"
                    onClick={apply}
                    disabled={saving || checked.size === 0}
                    startIcon={saving ? <CircularProgress size={14} color="inherit" /> : undefined}
                >
                    {t('ia.aplicarSelecionados')}
                </MDButton>
            </Box>
        </>
    );
};
